import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gatewayService } from 'crono-fe-common/hooks/crono-extension/gateway';
import { CronoGatewayLinkedinDownloadFileAction } from 'crono-fe-common/types/crono-extension/linkedin';
import SkeletonBlock from 'components/SkeletonBlock';
import {
  LINKEDIN_IMAGE_LABEL,
  parseLinkedinMessageContent,
} from 'utils/linkedinMessageContent';

// LinkedIn message images are only reachable with the viewer LinkedIn
// session, so they are downloaded through the extension
function useLinkedinMessageImage(url: string) {
  return useQuery({
    queryKey: ['linkedinMessageImage', url],
    queryFn: async () => {
      const result =
        await gatewayService.execute<CronoGatewayLinkedinDownloadFileAction>({
          target: 'linkedin',
          methodName: 'downloadFile',
          params: { url },
          identifiers: null,
        });
      if (!result?.urlData?.startsWith('data:image/')) {
        throw new Error('LinkedIn image could not be loaded');
      }
      return result.urlData;
    },
    staleTime: Infinity,
    cacheTime: 30 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function LinkedinMessageImage({
  url,
  renderText,
}: {
  url: string;
  renderText: (text: string) => ReactNode;
}) {
  const { data, isLoading, isError } = useLinkedinMessageImage(url);

  if (isLoading) {
    return <SkeletonBlock className="h-40 w-60 max-w-full" />;
  }
  if (isError || !data) {
    return <>{renderText(LINKEDIN_IMAGE_LABEL)}</>;
  }
  return (
    <img
      src={data}
      alt={LINKEDIN_IMAGE_LABEL}
      className="block max-w-full h-auto max-h-96 rounded-lg"
    />
  );
}

interface LinkedinMessageContentProps {
  content: string | null | undefined;
  renderText: (text: string) => ReactNode;
}

const LinkedinMessageContent = ({
  content,
  renderText,
}: LinkedinMessageContentProps) => {
  const segments = parseLinkedinMessageContent(content);

  return (
    <div className="flex flex-col gap-2">
      {segments.map((segment, index) =>
        segment.type === 'image' ? (
          <LinkedinMessageImage
            key={`image-${index}`}
            url={segment.url}
            renderText={renderText}
          />
        ) : (
          <div key={`text-${index}`}>{renderText(segment.text)}</div>
        ),
      )}
    </div>
  );
};

export default LinkedinMessageContent;
